import { useCallback, useEffect, useState } from 'react';

import isBot from 'Helpers/SEO/is-bot';

// Matches the breakpoint where .secondary-navbar (and so the mega menu triggers) is visible
const DESKTOP_QUERY = '(min-width: 992px)';

/**
 * useDeferredMegaMenu decides when the mega menu (and its /api/algolia/megamenu payload) should load.
 * - Bots load it immediately, so prerender snapshots keep the menu's product deep links.
 * - Desktop loads it once the page has loaded and the browser is idle.
 * - Narrower viewports, where the menu can't be opened, only load it if the viewport grows to desktop.
 * @return {Array} [shouldLoad, loadNow] - loadNow loads it straight away, e.g. on hover/focus intent
 */
export default function useDeferredMegaMenu() {
  const [shouldLoad, setShouldLoad] = useState(() => isBot());

  const loadNow = useCallback(() => setShouldLoad(true), []);

  useEffect(() => {
    if (shouldLoad) return undefined;

    const mediaQuery = window.matchMedia(DESKTOP_QUERY);
    let idleId;
    let timeoutId;

    // The viewport may have shrunk below desktop while waiting for load/idle
    const loadIfDesktop = () => {
      if (mediaQuery.matches) loadNow();
    };

    const scheduleIdle = () => {
      if (!mediaQuery.matches) return;
      if ('requestIdleCallback' in window) {
        idleId = window.requestIdleCallback(loadIfDesktop, { timeout: 5000 });
      } else {
        timeoutId = setTimeout(loadIfDesktop, 200);
      }
    };

    const onMediaChange = (e) => {
      if (e.matches) loadNow();
    };

    if (mediaQuery.matches) {
      if (document.readyState === 'complete') {
        scheduleIdle();
      } else {
        window.addEventListener('load', scheduleIdle, { once: true });
      }
    }

    // Always listen, so a viewport that becomes desktop (again) loads the menu
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', onMediaChange);
    } else {
      mediaQuery.addListener(onMediaChange);
    }

    return () => {
      window.removeEventListener('load', scheduleIdle);
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', onMediaChange);
      } else {
        mediaQuery.removeListener(onMediaChange);
      }
      if (idleId && 'cancelIdleCallback' in window) window.cancelIdleCallback(idleId);
      clearTimeout(timeoutId);
    };
  }, [shouldLoad, loadNow]);

  return [shouldLoad, loadNow];
}
